/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { type SkuAvailabilty } from '@/types/availability';
import { type SkuAddToCartConfig } from '../hooks/useCertonaAddToCartVisibility';

type AddToCartInfoParams = {
  skuDetailsData?: SkuAvailabilty[] | null;
  id?: string;
  productId?: string | number;
  partGroups?: string;
  inventoryConfig?: SkuAddToCartConfig;
  availableFulfillmentIds?: number[];
  leadingItemFulfillmentId?: number;
};

/**
 * Retrieves add to cart information for a specific SKU based on availability data and configuration.
 *
 * @param params - Configuration object containing SKU details and settings
 * @param params.skuDetailsData - Array of SKU availability data to search through
 * @param params.id - SKU ID to find in the availability data
 * @param params.productId - Product ID to use as fallback if not found in SKU details
 * @param params.thresholdHeaderText - Text that determines fulfillment method selection
 * @returns Object containing add to cart information including SKU ID, product ID, fulfillment method, and parts info, or empty object if SKU not found
 */
export const getAddToCartInfo = (params: AddToCartInfoParams) => {
  const {
    skuDetailsData,
    id,
    productId,
    partGroups,
    inventoryConfig,
    availableFulfillmentIds,
    leadingItemFulfillmentId,
  } = params;
  const skuDetails = skuDetailsData?.find((data: SkuAvailabilty) => {
    return data.skuId === id;
  });

  const bopusQuantity = skuDetails?.availabilityInfoVO.bopusQuantity;
  const sthQuantity = skuDetails?.availabilityInfoVO.sthQuantity;

  // If inventoryConfig is provided from the new inventories API, use its fulfillment decision
  // Otherwise fall back to existing logic using skuDetailsData
  let fulFillmentId = inventoryConfig?.fulfillmentId
    ? inventoryConfig.fulfillmentId
    : !!bopusQuantity && bopusQuantity > 0
      ? FULFILLMENT_METHODS.STOREORDER
      : !!sthQuantity && sthQuantity > 0
        ? FULFILLMENT_METHODS.ONLINEORDER
        : null;

  // Validate fulfillment against availableFulfillmentIds from API response
  // Prefer leading item's fulfillment type if available, otherwise use first available
  if (availableFulfillmentIds && availableFulfillmentIds.length > 0) {
    if (leadingItemFulfillmentId && availableFulfillmentIds.includes(leadingItemFulfillmentId)) {
      fulFillmentId = leadingItemFulfillmentId;
    } else {
      fulFillmentId = availableFulfillmentIds[0];
    }
  }

  const partsInfo = {
    partGroupId: partGroups ?? skuDetails?.attributesInfoVO?.partGroupId,
    partTerminologyId: skuDetails?.attributesInfoVO?.partTerminologyId,
  };

  const addToCartInfo = {
    skuId: skuDetails?.skuId ?? id,
    productId: skuDetails?.attributesInfoVO?.productId ?? productId?.toString() ?? partGroups,
    vehicleId: skuDetails?.catalogVehicleId ? skuDetails.catalogVehicleId : '',
    fulFillmentId: fulFillmentId,
    brandName: skuDetails?.attributesInfoVO?.brandName,
    productPartNumber: skuDetails?.attributesInfoVO?.productPartNumber,
    ...partsInfo,
  };

  return addToCartInfo;
};
